/**
 * Price-table — toegankelijke tab-interactie (WAI-ARIA tabs).
 *
 * Eerste blok-JS van het project; vestigt het patroon: het blok-JS leeft bij
 * het blok en wordt expliciet geïmporteerd in resources/js/main.js. Anders dan
 * de blok-SCSS — die is sinds de split een eigen entry per blok — zit het
 * blok-JS dus nog wél in de hoofdbundel. De module
 * self-init op DOM-ready en initialiseert elke blok-instance los; doet niets
 * wanneer het blok (of de tablist) afwezig is. Vanilla JS, geen dependencies.
 */

/**
 * Initialiseert één price-table-instance. Alle queries zijn binnen `root`
 * gescoped zodat meerdere blokken op één pagina elkaar niet besturen.
 *
 * @param {HTMLElement} root - De .price-table sectie.
 */
function initInstance(root) {
    const tablist = root.querySelector('.price-table__tabs[role="tablist"]');
    if (!tablist) {
        return;
    }

    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    if (tabs.length < 2) {
        return;
    }

    /**
     * Activeer een tab: roving tabindex + aria-selected, toon het bijbehorende
     * paneel en verberg de rest. Optioneel de focus verplaatsen.
     *
     * @param {HTMLElement} tab      - De te activeren tab.
     * @param {boolean}     setFocus - Focus naar de tab verplaatsen.
     */
    const activate = (tab, setFocus = true) => {
        tabs.forEach((item) => {
            const selected = item === tab;
            item.setAttribute('aria-selected', selected ? 'true' : 'false');
            item.setAttribute('tabindex', selected ? '0' : '-1');
            item.classList.toggle('price-table__tab--active', selected);

            const panel = root.querySelector('#' + item.getAttribute('aria-controls'));
            if (panel) {
                panel.hidden = !selected;
                panel.classList.toggle('price-table__panel--hidden', !selected);
            }
        });

        if (setFocus) {
            tab.focus();
        }
    };

    tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));

        tab.addEventListener('keydown', (event) => {
            let target = null;

            switch (event.key) {
                case 'ArrowRight':
                    target = tabs[(index + 1) % tabs.length];
                    break;
                case 'ArrowLeft':
                    target = tabs[(index - 1 + tabs.length) % tabs.length];
                    break;
                case 'Home':
                    target = tabs[0];
                    break;
                case 'End':
                    target = tabs[tabs.length - 1];
                    break;
                default:
                    return;
            }

            event.preventDefault();
            activate(target);
        });
    });
}

/**
 * Initialiseert alle price-table-blokken binnen een scope.
 *
 * @param {ParentNode} scope - Zoekwortel (default: document).
 */
export function initPriceTables(scope = document) {
    scope.querySelectorAll('.price-table').forEach(initInstance);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPriceTables());
} else {
    initPriceTables();
}
